import { CatalogSchema, type Catalog } from '@/types/card'
import { db } from '@/features/offline/db'

/**
 * Zugang zum Katalog.
 *
 * Diese Datei ist die einzige Stelle, an der die App den Kartenbestand bezieht.
 * Kommt später ein Login hinzu, bekommt genau diese Funktion einen Token — der Rest
 * der App bleibt unberührt.
 *
 * Auslieferung: Der Service Worker hält catalog.json vorgeladen. Zusätzlich wird jede
 * erfolgreich geladene Fassung in IndexedDB abgelegt. Diese Rückfallebene greift beim
 * Kaltstart ohne Netz, bevor der Service Worker das erste Mal aktiv war.
 */

/** Basisadresse der App — trägt die Auslieferung unter einem Unterverzeichnis mit. */
export const baseUrl = new URL(import.meta.env.BASE_URL, window.location.href).href

export interface CatalogResult {
  catalog: Catalog
  /** Woher die Daten stammen — die Oberfläche weist auf veraltete Stände hin. */
  source: 'netz' | 'lokal'
  fetchedAt: number
}

export async function loadCatalog(): Promise<CatalogResult> {
  try {
    const response = await fetch(new URL('catalog.json', baseUrl), { cache: 'no-cache' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const json = await response.text()
    const catalog = CatalogSchema.parse(JSON.parse(json))

    await db.catalogCache.put({ key: 'catalog', json, fetchedAt: Date.now() })
    return { catalog, source: 'netz', fetchedAt: Date.now() }
  } catch (networkError) {
    const cached = await db.catalogCache.get('catalog')
    if (!cached) {
      throw new Error(
        'Der Kartenkatalog ist nicht verfügbar. Bitte die App einmal mit Netzverbindung öffnen.',
        { cause: networkError },
      )
    }
    return {
      catalog: CatalogSchema.parse(JSON.parse(cached.json)),
      source: 'lokal',
      fetchedAt: cached.fetchedAt,
    }
  }
}

/** Lädt Suchindex und Volltexte. Beide sind vorgeladen und damit offline verfügbar. */
export async function loadSearchData(): Promise<{
  index: string
  fullTexts: Record<string, string>
}> {
  const [indexResponse, docsResponse] = await Promise.all([
    fetch(new URL('search-index.json', baseUrl)),
    fetch(new URL('search-docs.json', baseUrl)),
  ])
  if (!indexResponse.ok || !docsResponse.ok) {
    throw new Error('Der Suchindex konnte nicht geladen werden.')
  }
  return {
    index: await indexResponse.text(),
    fullTexts: (await docsResponse.json()) as Record<string, string>,
  }
}
